import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { SOFT_SIGNALS, SoftSignal, SoftSignalPayload } from 'models/Lead';
import { Button } from 'ncoded-component-library';
import SoftSignalIcon from 'components/SoftSignalIcon';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import DealUnsureSignalModal from '../DealUnsureSignalModal';
import DealDislikeSignalModal from '../DealDislikeSignalModal';
import ResolvePopup from 'components/ResolvePopup';
import { useTranslation } from 'react-i18next';
import useMatchMedia from 'hooks/useMatchMedia';

import './DealSoftSignals.styles.scss';

const POPUP_CONTENT = {
  [SOFT_SIGNALS.LIKE]: 'stillInterested',
  [SOFT_SIGNALS.UNSURE]: 'iHaveQuestion',
  [SOFT_SIGNALS.DISLIKE]: 'notInterestedAnymore',
};

type DealSoftSignalsProps = {
  className?: string;
  onSubmit: (
    values: SoftSignalPayload,
    isSkipPressed?: boolean,
  ) => Promise<void>;
};

const DealSoftSignals: React.FC<DealSoftSignalsProps> = (props) => {
  const { className, onSubmit } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [unsureModal, unsureModalRef] = useCallbackRef<ModalRef>();
  const [dislikeModal, dislikeModalRef] = useCallbackRef<ModalRef>();

  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames('deal-soft-signals', className);

  const { t } = useTranslation();

  const renderButton = useCallback(
    (softSignal: SoftSignal, onClick: () => void) => {
      const button = (
        <Button
          variant="icon"
          disabled={isLoading}
          className={classNames(softSignal)}
          onClick={onClick}
        >
          <SoftSignalIcon softSignal={softSignal} />
        </Button>
      );

      if (isPhablet) return button;

      return (
        <ResolvePopup onHover popupContent={t(POPUP_CONTENT[softSignal])}>
          {button}
        </ResolvePopup>
      );
    },
    [isLoading, isPhablet, t],
  );

  return (
    <div className={classes}>
      {renderButton(SOFT_SIGNALS.LIKE, async () => {
        setIsLoading(true);
        await onSubmit({ softSignal: SOFT_SIGNALS.LIKE });
        setIsLoading(false);
      })}
      {renderButton(SOFT_SIGNALS.UNSURE, unsureModal?.open)}
      {renderButton(SOFT_SIGNALS.DISLIKE, dislikeModal?.open)}

      <DealUnsureSignalModal ref={unsureModalRef} onSubmit={onSubmit} />
      <DealDislikeSignalModal ref={dislikeModalRef} onSubmit={onSubmit} />
    </div>
  );
};

export default DealSoftSignals;
